/* ============================================================
   PRO STUDIO BLOCKS — simpanan per akaun, satu doc setiap module
   ============================================================
   Rujukan penuh: PLAN_PROSTUDIO_BLOCKS.md (root).

   `users/{uid}/proBlocks/{moduleId}` = { moduleId, versi, blocks[], updatedAt }

   ⭐ SATU DOC SETIAP MODULE, bukan satu doc setiap block. Sebabnya:
      susunan block IALAH susunan prompt akhir — senarai dalam satu doc
      menyimpan susunan itu secara semula jadi, Save menjadi atomik
      (semua atau tiada), dan server hanya perlu SATU bacaan semasa
      Generate. Blocks terhad (≤30), jadi doc kekal jauh di bawah 1 MB.

   ⛔ BUKAN medan dalam doc `users` — doc itu ada had keras 1 MB, dan
      data yang boleh membesar tidak masuk situ (corak `ctaTemplates`).

   ⛔ NILAI DISEMAK SEBELUM DITULIS (prinsip 4 CLAUDE.md). Block tanpa
      nama, kandungan kosong, terlalu panjang, atau id berulang DITOLAK
      dengan mesej yang menamakan block itu — bukan ditulis rosak lalu
      Generate gagal senyap tiga minggu kemudian.

   ⚠️ Had yang sama dikuatkuasakan sekali lagi di SERVER sebelum block
      dipakai. Dua tempat bukan pertindihan sia-sia: server tidak boleh
      percaya apa yang client tulis.
   ============================================================ */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

export const PRO_BLOK_COL = 'proBlocks';

export const PRO_BLOK_HAD = {
    blok: 30,        // bilangan block setiap module
    nama: 80,        // aksara nama block
    teks: 15000,     // aksara setiap kotak (Instruction / Example / Prompt)
    jumlah: 200000,  // aksara semua kotak semua block
};

export const MOD_SAH = ['ai', 'exact'];

/* Bentuk yang dibandingkan untuk "ada perubahan belum disimpan" dan
   bentuk yang ditulis ke doc — SATU takrifan supaya pembanding tidak
   terpesong daripada apa yang benar-benar disimpan. */
const bentukBlok = (b) => ({
    id: String(b?.id ?? ''),
    nama: String(b?.nama ?? ''),
    mod: MOD_SAH.includes(b?.mod) ? b.mod : 'ai',
    arahan: String(b?.arahan ?? ''),
    contoh: String(b?.contoh ?? ''),
    teks: String(b?.teks ?? ''),
});

export const cetakBlok = (senarai) => JSON.stringify((Array.isArray(senarai) ? senarai : []).map(bentukBlok));

/* Semak + bersihkan. MELONTAR ralat yang menamakan block bermasalah.
   Pulangkan senarai bersih yang selamat ditulis. */
export function bersihkanBlok(senarai) {
    if (!Array.isArray(senarai)) throw new Error('Senarai block tidak sah.');
    if (senarai.length === 0) throw new Error('Tiada block untuk disimpan. Tambah sekurang-kurangnya satu block.');
    if (senarai.length > PRO_BLOK_HAD.blok) {
        throw new Error(`Terlalu banyak block (${senarai.length}). Had ${PRO_BLOK_HAD.blok} block setiap module.`);
    }

    const idDilihat = new Set();
    let jumlah = 0;

    const bersih = senarai.map((asal, i) => {
        const b = bentukBlok(asal);
        const kedudukan = `Block #${i + 1}`;

        const id = b.id.trim();
        if (!id || idDilihat.has(id)) throw new Error(`${kedudukan} tiada id unik. Muat semula halaman dan cuba lagi.`);
        idDilihat.add(id);

        const nama = b.nama.replace(/\s+/g, ' ').trim();
        if (!nama) throw new Error(`${kedudukan} tiada nama. Isi Block name.`);
        if (nama.length > PRO_BLOK_HAD.nama) {
            throw new Error(`Nama block "${nama.slice(0, 30)}…" terlalu panjang (${nama.length}). Had ${PRO_BLOK_HAD.nama} aksara.`);
        }

        const semakPanjang = (label, t) => {
            if (t.length > PRO_BLOK_HAD.teks) {
                throw new Error(`${label} pada block "${nama}" terlalu panjang (${t.length.toLocaleString()} aksara). Had ${PRO_BLOK_HAD.teks.toLocaleString()}.`);
            }
            return t;
        };
        const arahan = semakPanjang('Instruction', b.arahan);
        const contoh = semakPanjang('Example', b.contoh);
        const teks = semakPanjang('Prompt', b.teks);

        if (b.mod === 'ai' && !arahan.trim()) {
            throw new Error(`Block "${nama}" kosong. Isi Instruction, atau padam block itu.`);
        }
        if (b.mod === 'exact' && !teks.trim()) {
            throw new Error(`Block "${nama}" (Use exactly) kosong. Isi Prompt, atau padam block itu.`);
        }

        jumlah += arahan.length + contoh.length + teks.length;
        return { id, nama, mod: b.mod, arahan, contoh, teks };
    });

    if (jumlah > PRO_BLOK_HAD.jumlah) {
        throw new Error(`Jumlah teks semua block (${jumlah.toLocaleString()} aksara) melebihi had ${PRO_BLOK_HAD.jumlah.toLocaleString()}.`);
    }
    return bersih;
}

const uidSemasa = () => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Belum log masuk');
    return uid;
};

/* Pulang { blocks, updatedAt, versi } atau null bila belum pernah disimpan. */
export async function bacaProBlok(moduleId) {
    const uid = uidSemasa();
    const snap = await getDoc(doc(db, 'users', uid, PRO_BLOK_COL, moduleId));
    if (!snap.exists()) return null;
    const d = snap.data() || {};
    return {
        blocks: Array.isArray(d.blocks) ? d.blocks.map(bentukBlok) : [],
        updatedAt: d.updatedAt?.toDate ? d.updatedAt.toDate() : null,
        versi: Number.isFinite(d.versi) ? d.versi : 1,
    };
}

/* Tulis SEMUA block (ganti penuh — susunan ialah data). Pulangkan
   senarai bersih yang benar-benar ditulis, supaya skrin memaparkan
   nilai yang sama seperti dalam doc. */
export async function simpanProBlok(moduleId, senarai) {
    const uid = uidSemasa();
    const blocks = bersihkanBlok(senarai);
    await setDoc(doc(db, 'users', uid, PRO_BLOK_COL, moduleId), {
        moduleId,
        versi: 1,
        blocks,
        updatedAt: serverTimestamp(),
    });
    return blocks;
}
